/**
 * API Client for Asana Recurrence Manager
 *
 * This module provides:
 * - Axios instance with base URL and interceptors
 * - Type definitions for all API entities
 * - Functions for all API endpoints
 *
 * Usage:
 *   import { login, getTasks, createTask } from './api/client';
 */

import axios from 'axios';

// =============================================================================
// Configuration
// =============================================================================

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:8000/api';

/**
 * Axios instance configured for the API
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
  },
});

// Add auth token to all requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors by redirecting to login
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// =============================================================================
// Type Definitions
// =============================================================================

/** Login request payload */
export interface LoginRequest {
  username: string;
  password: string;
}

/** Login response with JWT token */
export interface LoginResponse {
  access_token: string;
  token_type: string;
}

/** Application settings */
export interface Settings {
  id: number;
  asana_api_key: string | null;
  workspace_gid: string | null;
}

/** Response from testing Asana connection */
export interface TestConnectionResponse {
  success: boolean;
  message: string;
  workspaces?: { gid: string; name: string }[];
}

/** Recurring task entity */
export interface RecurringTask {
  id: number;
  name: string;
  recurrence_type: string;  // 'daily' | 'weekly' | 'monthly'
  recurrence_day: number | null;  // 0-6 for weekly, 1-31 for monthly
  project_gid: string;
  project_name: string | null;
  section_gid: string | null;
  section_name: string | null;
  assignee_gid: string | null;
  assignee_name: string | null;
  due_date: string | null;  // ISO date string
  created_at: string | null;
  last_success: string | null;
  next_run: string | null;
}

/** Asana project */
export interface AsanaProject {
  gid: string;
  name: string;
}

/** Asana section (board column) */
export interface AsanaSection {
  gid: string;
  name: string;
}

/** Asana user */
export interface AsanaUser {
  gid: string;
  name: string;
  email?: string;
}

/** Asana workspace */
export interface AsanaWorkspace {
  gid: string;
  name: string;
}

// =============================================================================
// Authentication API
// =============================================================================

/**
 * Authenticate user and get JWT token
 */
export const login = (data: LoginRequest) =>
  apiClient.post<LoginResponse>('/login', data);

// =============================================================================
// Settings API
// =============================================================================

/**
 * Get current application settings
 */
export const getSettings = () =>
  apiClient.get<Settings>('/settings');

/**
 * Update application settings
 */
export const updateSettings = (data: Partial<Settings>) =>
  apiClient.put<Settings>('/settings', data);

/**
 * Test Asana API connection
 */
export const testConnection = () =>
  apiClient.post<TestConnectionResponse>('/settings/test');

// =============================================================================
// Tasks API
// =============================================================================

/**
 * Get all recurring tasks
 */
export const getTasks = () =>
  apiClient.get<RecurringTask[]>('/tasks');

/**
 * Create a new recurring task
 */
export const createTask = (data: Omit<RecurringTask, 'id' | 'created_at' | 'last_success' | 'next_run'>) =>
  apiClient.post<RecurringTask>('/tasks', data);

/**
 * Update an existing recurring task
 */
export const updateTask = (id: number, data: Omit<RecurringTask, 'id' | 'created_at' | 'last_success' | 'next_run'>) =>
  apiClient.put<RecurringTask>(`/tasks/${id}`, data);

/**
 * Delete a recurring task
 */
export const deleteTask = (id: number) =>
  apiClient.delete(`/tasks/${id}`);

/**
 * Manually trigger a task to create in Asana now
 */
export const triggerTask = (id: number) =>
  apiClient.post(`/tasks/${id}/trigger`);

// =============================================================================
// Asana Proxy API
// =============================================================================

/**
 * Get all accessible Asana workspaces
 */
export const getWorkspaces = () =>
  apiClient.get<AsanaWorkspace[]>('/asana/workspaces');

/**
 * Get all projects in the configured workspace
 */
export const getProjects = () =>
  apiClient.get<AsanaProject[]>('/asana/projects');

/**
 * Get all sections in a project
 */
export const getSections = (projectGid: string) =>
  apiClient.get<AsanaSection[]>(`/asana/projects/${projectGid}/sections`);

/**
 * Get all users in the configured workspace
 */
export const getUsers = () =>
  apiClient.get<AsanaUser[]>('/asana/users');

export default apiClient;
